import Service, { inject as service } from '@ember/service';
import { equal } from '@ember/object/computed';
import Cookies from 'js-cookie';
import ApiService, { ApiError } from 'cio/services/api';
import RecaptchaService from 'cio/services/recaptcha';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';
import { getGoogleAnalyticsClientID } from 'cio/utils/get-analytics-meta-properties';
import { DataCenter } from 'cio/models/account';
import FeaturesService from 'ember-feature-flags/services/features';
import { task } from 'ember-concurrency';
import urlBuilder from 'cio/utils/url-builder';
import { ProductApp } from 'cio/utils/product-app-info';

export const ALLOWED_SIGNUP_TYPES = [ProductApp.JOURNEYS, ProductApp.DATA_PIPELINES];

type BaseSignupArgs = {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  acceptTerms: boolean;
  useCase: string;
  useCaseDetail: string;
  workPhone: string;
  dataCenter?: DataCenter;
  role: string;
};

type IdentifyAttrs = Partial<
  BaseSignupArgs & {
    createdAt: number;
    startedSignupAt: number;
    employeeCount: string;
    cdpPlatform?: string;
    cdpPlatformOther?: string;
    signupType: string;
  }
>;

export type RegistrationAttrs = BaseSignupArgs & {
  password: string;
  passwordConfirmation: string;
  employeeCount: string;
  cdpPlatform: string;
  cdpPlatformOther: string;
  signupType: string;
  roleOther: string;
  channels: string[];
  channelsOther: string;
  automationExperience: string;
  integrationExperience: string;
  testDeliveryMode?: boolean;
};

export type SignupRoute =
  | 'signup.index'
  | 'signup.password'
  | 'signup.company'
  | 'authenticated.post_registration.goal'
  | 'authenticated.post_registration.ready';

export type PersonalizationSignupRoute =
  | 'signup.index'
  | 'signup.password'
  | 'signup.company'
  | 'authenticated.post_registration.role'
  | 'authenticated.post_registration.goal'
  | 'authenticated.post_registration.channels'
  | 'authenticated.post_registration.tooling'
  | 'authenticated.post_registration.connect';

export default class SignupService extends Service {
  @service api!: ApiService;
  @service recaptcha!: RecaptchaService;
  @service store!: DS.Store;
  @service router!: RouterService;
  @service session!: any;
  @service features!: FeaturesService;

  @equal('router.currentRouteName', 'signup.index') isEnteringDetails!: boolean;
  @equal('router.currentRouteName', 'signup.password') isEnteringPassword!: boolean;
  @equal('router.currentRouteName', 'signup.check-email') isAskedToCheckEmail!: boolean;
  @equal('router.currentRouteName', 'signup.company') isEnteringCompanyDetails!: boolean;
  @equal('router.currentRouteName', 'authenticated.post_registration.goal') isEnteringGoals!: boolean;
  @equal('router.currentRouteName', 'authenticated.post_registration.ready') isReady!: boolean;

  async initSignup(email: string) {
    let queryString = `?email=${encodeURIComponent(email)}`;

    let recaptchaToken = await this.recaptcha.execute('signup');
    // recaptchaToken is empty in test and development:
    if (recaptchaToken) {
      queryString += `&g-recaptcha-response=${encodeURIComponent(recaptchaToken)}`;
    }

    try {
      let res = await this.api.request(`account_signup/start${queryString}`, {
        pathScope: 'global',
        dataCenter: DataCenter.US,
        ignoreErrors: true,
      });

      return {
        role: res.signup_prepopulate_data.role,
        companyName: res.signup_prepopulate_data.company_name,
      };
    } catch (err) {
      let errors = err.errors || [];
      if (errors.some((e: ApiError) => e.status === '409')) {
        return false;
      }

      throw err;
    }
  }

  trackIdentify({
    email,
    createdAt,
    startedSignupAt,
    firstName,
    lastName,
    companyName,
    role,
    useCase,
    useCaseDetail,
    employeeCount,
    workPhone,
    acceptTerms,
    signupType,
    cdpPlatform,
    cdpPlatformOther,
  }: IdentifyAttrs) {
    let traits: Partial<Record<string, string | number | boolean>> = {
      email,
      created_at: createdAt,
      started_signup_at: startedSignupAt,
      first_name: firstName,
      last_name: lastName,
      company_name: companyName,
      role,
      use_case: useCase,
      use_case_detail: useCaseDetail,
      employee_count: employeeCount,
      work_phone: workPhone,
      signup_type: signupType,
      ...(signupType === ProductApp.DATA_PIPELINES && {
        cdp_platform: cdpPlatform,
        cdp_platform_other: cdpPlatformOther,
      }),
    };

    if (typeof acceptTerms !== 'undefined') {
      traits.accept_terms = String(acceptTerms);
    }

    return this.api.post('account_signup/track_identify', {
      pathScope: 'global',
      dataCenter: DataCenter.US,
      ignoreErrors: true,
      data: { email, traits },
    });
  }

  async register({
    values: {
      email,
      firstName,
      lastName,
      password,
      companyName,
      acceptTerms,
      employeeCount,
      signupType,
      cdpPlatform,
      cdpPlatformOther,
      useCase,
      useCaseDetail,
      workPhone,
      role,
      dataCenter,
      testDeliveryMode,
    },
  }: {
    values: RegistrationAttrs;
  }) {
    let queryString = '';

    let recaptchaToken = await this.recaptcha.execute('signup_signup');
    // recaptchaToken is empty in test and development:
    if (recaptchaToken) {
      queryString = `?g-recaptcha-response=${encodeURIComponent(recaptchaToken)}`;
    }

    return this.api
      .post(`account_signup/signup${queryString}`, {
        pathScope: 'global',
        dataCenter: DataCenter.US,
        ignoreErrors: true,
        data: {
          email,
          invoice_email: email,
          legal_email: email,
          first_name: firstName,
          last_name: lastName,
          password,
          name: companyName,
          data_center: dataCenter,
          meta: {
            email,
            invoice_email: email,
            legal_email: email,
            first_name: firstName,
            last_name: lastName,
            company: companyName,
            data_center: dataCenter,
            signup_use_case: useCase,
            signup_use_case_detail: useCaseDetail,
            signup_employee_count: employeeCount,
            signup_type: signupType,
            signup_cdp_platform: cdpPlatform,
            signup_cdp_platform_other: cdpPlatformOther,
            signup_phone: workPhone,
            signup_role: role,
            signup_accepted_terms: acceptTerms,
            signup_attrs: {
              form_source: 'new-cio-account',
            },
            test_delivery_mode: testDeliveryMode,
            cookies: this._referrerRequestParams(),
          },
        },
      })
      .then(({ account, user, access_token, session_expires_at }) => {
        this.store.pushPayload({ account, user });
        return { account, accessToken: access_token, sessionExpiresAt: session_expires_at, user };
      });
  }

  confirmAccount({ confirmationCode }: { confirmationCode: string }) {
    return this.api.post('account_signup/confirm_account', {
      pathScope: 'global',
      dataCenter: DataCenter.US,
      ignoreErrors: true,
      data: { confirmation_code: confirmationCode },
    });
  }

  postRegistration({
    senderEmail,
    senderName,
    selectedFeedbackOption,
    comments,
    automationExperience,
    integrationExperience,
    useCase,
    useCaseDetail,
    role,
    channels,
  }: Partial<{
    senderEmail: string;
    senderName: string;
    selectedFeedbackOption: string;
    comments: string;
    automationExperience: string;
    integrationExperience: string;
    useCase: string;
    useCaseDetail: string;
    role: string;
    channels: string[];
  }>) {
    return this.api.post('account_signup/post_registration', {
      pathScope: 'account',
      dataCenter: DataCenter.US,
      ignoreErrors: true,
      data: {
        sender_email: senderEmail,
        sender_name: senderName,
        selected_feedback_option: selectedFeedbackOption,
        comments,
        signup_automation_experience: automationExperience,
        signup_integration_experience: integrationExperience,
        signup_use_case: useCase,
        signup_use_case_detail: useCaseDetail,
        signup_role: role,
        signup_channels: channels,
      },
    });
  }

  _referrerRequestParams() {
    return {
      referrer: document.referrer,
      ajs_id: Cookies.get('ajs_anonymous_id'),
      ga_client_id: getGoogleAnalyticsClientID(),
      first_touch_referrer: Cookies.get('cioFT'),
      last_touch_referrer: Cookies.get('cioLT'),
      first_touch_domain: this._extractDomain(Cookies.get('cioFT')),
      last_touch_domain: this._extractDomain(Cookies.get('cioLT')),
    };
  }

  validateSignupType = task({ enqueue: true }, async (signupType: string | undefined) => {
    let validatedSignupType = signupType ?? ProductApp.JOURNEYS;

    if (!ALLOWED_SIGNUP_TYPES.includes((validatedSignupType as ProductApp) ?? '')) {
      validatedSignupType = ProductApp.JOURNEYS;
    }

    return {
      name: validatedSignupType,

      // Boolean values for easier use on templates
      isJourneys: validatedSignupType === ProductApp.JOURNEYS,
      isDataPipelines: validatedSignupType === ProductApp.DATA_PIPELINES,
    };
  });

  getTestimonials = task(async (signupType: string, category: string) => {
    // Hardcoded CDP testimonial for now, as it might not have testimonials at all,
    // this is a temporary handpicked data-focused one that should be removed soon
    if (signupType === ProductApp.DATA_PIPELINES) {
      /* eslint-disable spellcheck/spell-checker */
      return [
        {
          category: 'cdp',
          author: 'Renan Serrano',
          quote:
            'It took a skilled engineer months to connect data to our old (and expensive) CDP provider. Even syncing with Meta and Amplitude required many support emails. Customer.io Data Pipelines solves this in a few clicks without any additional costs.',
          role: 'Founder of Visto.bio',
        },
      ];
      /* eslint-enable spellcheck/spell-checker */
    }

    return this.api.request(urlBuilder('account_signup/testimonials', { category }), {
      pathScope: 'global',
      dataCenter: DataCenter.US,
    });
  });

  // based on https://stackoverflow.com/a/23945027 :
  _extractDomain(url: string | undefined) {
    if (!url) {
      return;
    }

    let hostname;

    if (url.includes('//')) {
      hostname = url.split('/')[2];
    } else {
      hostname = url.split('/')[0];
    }

    // find & remove port number
    hostname = hostname.split(':')[0];
    // find & remove "?", potential URL params
    hostname = hostname.split('?')[0];

    return hostname.toLowerCase();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    signup: SignupService;
  }
}
